import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createPortal } from "react-dom";

const DateTimeInputwithcal = ({
    label,
    name,
    value,
    onChange,
    placeholder = "DD MMM YYYY HH:mm",
    onError,
    required = false,
    disabled = false
}) => {
    const [displayValue, setDisplayValue] = useState("");
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [error, setError] = useState("");
    const isInternalChange = useRef(false);
    const inputRef = useRef(null);
    const datePickerRef = useRef(null);
    const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

    // Display format: "12 May 2026 09:00"
    const DISPLAY_FORMAT = "DD MMM YYYY HH:mm";
    // Storage format (compatible with backend)
    const STORAGE_FORMAT = "YYYY-MM-DDTHH:mm:ss";

    // Update display value when external value prop changes (not from internal updates)
    useEffect(() => {
        if (!isInternalChange.current) {
            if (value) {
                const m = moment(value);
                if (m.isValid()) {
                    setDisplayValue(m.format(DISPLAY_FORMAT));
                    setSelectedDate(m.toDate());
                } else {
                    setDisplayValue("");
                    setSelectedDate(null);
                }
            } else {
                setDisplayValue("");
                setSelectedDate(null);
            }
        }
    }, [value, DISPLAY_FORMAT]);

    // Update position function
    const updatePosition = () => {
        if (!inputRef.current) return;
        
        const rect = inputRef.current.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
        
        const pickerWidth = 380; // Width for datetime picker (date + time)
        const pickerHeight = 400;
        
        // Position below the input
        let left = rect.left + scrollLeft;
        let top = rect.bottom + scrollTop + 5;
        
        // Check if it goes off screen on the right
        if (left + pickerWidth > window.innerWidth + scrollLeft) {
            left = window.innerWidth + scrollLeft - pickerWidth - 10;
        }
        
        // Check if it goes off screen on the left
        if (left < scrollLeft + 10) {
            left = scrollLeft + 10;
        }
        
        // Check if it goes off screen at the bottom
        if (rect.bottom + pickerHeight + scrollTop > window.innerHeight + scrollTop) {
            // Position above the input
            top = rect.top + scrollTop - pickerHeight - 5;
        }
        
        setPickerPosition({ top, left });
    };

    // Close date picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                datePickerRef.current &&
                !datePickerRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setShowDatePicker(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Handle scroll and resize
    useEffect(() => {
        if (showDatePicker) {
            updatePosition();
            window.addEventListener("scroll", updatePosition, true);
            window.addEventListener("resize", updatePosition);
            
            return () => {
                window.removeEventListener("scroll", updatePosition, true);
                window.removeEventListener("resize", updatePosition);
            };
        }
    }, [showDatePicker]);

    // Parse date function (kept the same)
    const parseDate = (dateStr) => {
        if (!dateStr || typeof dateStr !== "string") return moment(null);

        let trimmed = dateStr.trim();
        if (trimmed === "") return moment(null);

        const compactDateMatch = trimmed.match(/^(\d{2})(\d{2})(\d{4})(?:\s+(\d{1,2})(?::?(\d{2}))?)?$/);
        if (compactDateMatch) {
            const day = compactDateMatch[1];
            const month = compactDateMatch[2];
            const year = compactDateMatch[3];
            let hour = compactDateMatch[4] || "00";
            let minute = compactDateMatch[5] || "00";
            hour = hour.padStart(2, "0");
            minute = minute.padStart(2, "0");
            trimmed = `${year}-${month}-${day} ${hour}:${minute}`;
        }

        const formats = [
            "DD MMM YYYY HH:mm",
            "DD MMM YYYY H:mm",
            "DD MMM YYYY HH:mm:ss",
            "YYYY-MM-DD HH:mm:ss",
            "YYYY-MM-DD HH:mm",
            "YYYY-MM-DDTHH:mm:ss",
            "YYYY-MM-DDTHH:mm",
            "YYYY-MM-DD",
            "DD/MM/YYYY HH:mm:ss",
            "DD/MM/YYYY HH:mm",
            "DD/MM/YYYY",
            "DD-MM-YYYY HH:mm:ss",
            "DD-MM-YYYY HH:mm",
            "DD-MM-YYYY",
            "MM-DD-YYYY HH:mm:ss",
            "MM-DD-YYYY HH:mm",
            "MM-DD-YYYY",
            "DD MMM YYYY",
            "D MMM YYYY HH:mm",
            "D MMM YYYY",
            "DDMMYYYY HH:mm",
            "DDMMYYYY HHmm",
            "DDMMYYYY"
        ];

        for (let format of formats) {
            const m = moment(trimmed, format, true);
            if (m.isValid()) {
                return m;
            }
        }

        const fallback = moment(trimmed);
        if (fallback.isValid()) {
            return fallback;
        }

        return moment(null);
    };

    const validateAndSync = (inputValue, silent = false) => {
        if (!inputValue.trim()) {
            if (required && !silent) {
                setError("This field is required");
                onError?.(name, true);
            } else {
                setError("");
                onError?.(name, false);
            }
            return null;
        }

        const m = parseDate(inputValue);
        if (m.isValid()) {
            if (!silent) {
                setError("");
                onError?.(name, false);
            }
            return m;
        } else {
            if (!silent) {
                setError("Invalid date. Expected format: DD MMM YYYY HH:mm (e.g., 12 May 2026 09:00)");
                onError?.(name, true);
            }
            return null;
        }
    };

    const updateFromMoment = (m, shouldTriggerOnChange = true) => {
        if (!m || !m.isValid()) {
            setDisplayValue("");
            setSelectedDate(null);
            setError("");
            if (shouldTriggerOnChange) {
                onChange(name, null);
            }
            return false;
        }

        const displayFormatted = m.format(DISPLAY_FORMAT);
        const storageFormatted = m.format(STORAGE_FORMAT);

        setDisplayValue(displayFormatted);
        setSelectedDate(m.toDate());
        setError("");
        onError?.(name, false);

        if (shouldTriggerOnChange) {
            isInternalChange.current = true;
            onChange(name, storageFormatted);
            setTimeout(() => {
                isInternalChange.current = false;
            }, 50);
        }
        return true;
    };

    const handleDatePickerChange = (date) => {
        if (!date) {
            updateFromMoment(null, true);
            setShowDatePicker(false);
            return;
        }
        const m = moment(date);
        updateFromMoment(m, true);
        setShowDatePicker(false);
    };

    const handleInputChange = (e) => {
        const rawValue = e.target.value;
        setDisplayValue(rawValue);
        validateAndSync(rawValue, true);
    };

    const handleBlur = () => {
        if (!displayValue.trim()) {
            updateFromMoment(null, true);
            return;
        }

        const m = parseDate(displayValue);
        if (m && m.isValid()) {
            updateFromMoment(m, true);
        } else {
            setError("Invalid date. Use format: DD MMM YYYY HH:mm (e.g., 12 May 2026 09:00)");
            onError?.(name, true);
        }
    };

    const handleFocus = () => {
        setShowDatePicker(true);
        setTimeout(updatePosition, 0);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.target.blur();
        }
        if (e.key === 'Escape') {
            setShowDatePicker(false);
        }
    };

    const selectToday = () => {
        const now = moment();
        updateFromMoment(now, true);
        setShowDatePicker(false);
    };

    const clearDate = () => {
        updateFromMoment(null, true);
        setShowDatePicker(false);
    };

    // Styles
    const customStyles = `
    .react-datepicker {
      font-family: inherit;
      border: none;
      background-color: transparent;
      display: flex !important;
      flex-wrap: wrap;
    }
    
    @media (max-width: 480px) {
      .react-datepicker {
        flex-direction: column;
      }
      .react-datepicker__month-container {
        border-right: none !important;
        border-bottom: 1px solid #e2e8f0;
      }
      .react-datepicker__time-container {
        width: 100% !important;
        border-left: none !important;
      }
      .react-datepicker__time-container .react-datepicker__time-box {
        width: 100% !important;
      }
      .react-datepicker__time-list {
        display: flex;
        flex-wrap: wrap;
        height: auto !important;
        max-height: 200px;
        overflow-y: auto;
      }
      .react-datepicker__time-list-item {
        display: inline-block;
        width: 25%;
        text-align: center;
      }
    }
    
    .react-datepicker__month-container {
      float: none;
      border-right: 1px solid #e2e8f0;
    }
    
    .dark .react-datepicker__month-container {
      border-right: 1px solid #334155;
    }
    
    .react-datepicker__header {
      background-color: transparent;
      border-bottom: 1px solid #e2e8f0;
      padding: 0.5rem 0.5rem 0 0.5rem;
    }
    
    .react-datepicker__current-month {
      font-weight: 600;
      font-size: 0.875rem;
      margin-bottom: 0.5rem;
    }
    
    .react-datepicker__day-names {
      margin-bottom: 0;
    }
    
    .react-datepicker__day-name {
      color: #64748b;
      font-size: 0.75rem;
      width: 2rem;
      line-height: 2rem;
      margin: 0.1rem;
    }
    
    .react-datepicker__week {
      display: flex;
      justify-content: center;
    }
    
    .react-datepicker__day {
      width: 2rem;
      line-height: 2rem;
      border-radius: 9999px;
      margin: 0.1rem;
      font-size: 0.875rem;
      color: #1e293b;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
    
    .react-datepicker__day:hover {
      background-color: #f1f5f9;
      border-radius: 9999px;
    }
    
    .react-datepicker__day--selected {
      background-color: #3b82f6;
      color: white;
    }
    
    .react-datepicker__day--selected:hover {
      background-color: #2563eb;
    }
    
    .react-datepicker__day--today {
      font-weight: bold;
      color: #3b82f6;
    }
    
    .react-datepicker__day--outside-month {
      color: #cbd5e1;
    }
    
    .react-datepicker__navigation {
      top: 0.75rem;
    }
    
    .react-datepicker__navigation--previous {
      left: 0.5rem;
      border-right-color: #64748b;
    }
    
    .react-datepicker__navigation--next {
      right: 0.5rem;
      border-left-color: #64748b;
    }
    
    .react-datepicker__time-container {
      border-left: 1px solid #e2e8f0;
      width: 100px;
      float: none;
      display: flex;
      flex-direction: column;
    }
    
    .dark .react-datepicker__time-container {
      border-left-color: #334155;
    }
    
    .react-datepicker__time-container .react-datepicker__header {
      border-bottom: 1px solid #e2e8f0;
      padding: 0.5rem 0;
      text-align: center;
    }
    
    .react-datepicker__time-container .react-datepicker__time {
      background: transparent;
      height: 100%;
    }
    
    .react-datepicker__time-container .react-datepicker__time-box {
      width: 100px !important;
    }
    
    .react-datepicker__time-list {
      height: 260px !important;
      overflow-y: auto;
      padding: 0 !important;
      margin: 0 !important;
    }
    
    .react-datepicker__time-list-item {
      padding: 8px 10px !important;
      font-size: 0.8rem;
      text-align: center;
      white-space: nowrap;
      list-style: none;
      height: auto !important;
      line-height: 1.2 !important;
    }
    
    .react-datepicker__time-list-item--selected {
      background-color: #3b82f6 !important;
      color: white !important;
    }
    
    .react-datepicker__time-list-item:hover {
      background-color: #f1f5f9;
    }
    
    .dark .react-datepicker {
      background-color: #1e293b;
    }
    
    .dark .react-datepicker__header {
      background-color: #1e293b;
      border-bottom-color: #334155;
    }
    
    .dark .react-datepicker__current-month {
      color: #f1f5f9;
    }
    
    .dark .react-datepicker__day-name {
      color: #94a3b8;
    }
    
    .dark .react-datepicker__day {
      color: #e2e8f0;
    }
    
    .dark .react-datepicker__day:hover {
      background-color: #334155;
    }
    
    .dark .react-datepicker__day--selected {
      background-color: #3b82f6;
      color: white;
    }
    
    .dark .react-datepicker__day--today {
      color: #60a5fa;
    }
    
    .dark .react-datepicker__day--outside-month {
      color: #475569;
    }
    
    .dark .react-datepicker__time-list-item {
      color: #e2e8f0;
    }
    
    .dark .react-datepicker__time-list-item:hover {
      background-color: #334155;
    }
    
    .today-clear-buttons {
      display: flex;
      justify-content: space-between;
      padding: 0.5rem;
      border-top: 1px solid #e2e8f0;
      gap: 0.5rem;
    }
    
    .dark .today-clear-buttons {
      border-top-color: #334155;
    }
    
    .btn-today {
      flex: 1;
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
      font-weight: 500;
      background-color: #3b82f6;
      color: white;
      border-radius: 0.375rem;
      transition: background-color 0.15s ease;
    }
    
    .btn-today:hover {
      background-color: #2563eb;
    }
    
    .btn-clear {
      flex: 1;
      padding: 0.375rem 0.75rem;
      font-size: 0.75rem;
      font-weight: 500;
      background-color: #f1f5f9;
      color: #1e293b;
      border-radius: 0.375rem;
      transition: background-color 0.15s ease;
    }
    
    .dark .btn-clear {
      background-color: #334155;
      color: #e2e8f0;
    }
    
    .btn-clear:hover {
      background-color: #e2e8f0;
    }
    
    .dark .btn-clear:hover {
      background-color: #475569;
    }
    `;

    return (
        <>
            <style>{customStyles}</style>
            <div className="w-full relative">
                <div className="relative">
                    <input
                        ref={inputRef}
                        type="text"
                        name={name}
                        value={displayValue}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        onFocus={handleFocus}
                        onKeyDown={handleKeyDown}
                        placeholder={placeholder}
                        disabled={disabled}
                        autoComplete="off"
                        className={`w-full px-3 py-2 text-sm border rounded-lg pr-10
                            bg-white dark:bg-slate-800 
                            text-slate-900 dark:text-white 
                            focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF]
                            transition-colors duration-200
                            ${error ? "border-red-500" : "border-slate-300 dark:border-slate-700"}
                            ${disabled ? "opacity-60 cursor-not-allowed bg-slate-100 dark:bg-slate-900" : ""}`}
                    />
                    <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                        onClick={() => !disabled && setShowDatePicker(!showDatePicker)}
                        disabled={disabled}
                        aria-label="Toggle calendar"
                    >
                        📅
                    </button>
                </div>

                {showDatePicker && !disabled && createPortal(
                    <div
                        ref={datePickerRef}
                        style={{
                            position: "absolute",
                            top: pickerPosition.top,
                            left: pickerPosition.left,
                            zIndex: 99999
                        }}
                    >
                        <div className="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg shadow-lg overflow-hidden">
                            <DatePicker
                                selected={selectedDate}
                                onChange={handleDatePickerChange}
                                inline
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={5}
                                timeCaption="Time"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                dateFormat="dd MMM yyyy HH:mm"
                                className="border-0"
                                wrapperClassName="w-full"
                                calendarClassName="border-0 !bg-transparent"
                                renderCustomHeader={({
                                    date,
                                    decreaseMonth,
                                    increaseMonth,
                                    prevMonthButtonDisabled,
                                    nextMonthButtonDisabled,
                                }) => (
                                    <div className="flex justify-between items-center px-2 py-1">
                                        <button
                                            onClick={decreaseMonth}
                                            disabled={prevMonthButtonDisabled}
                                            type="button"
                                            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </button>
                                        <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                                            {moment(date).format("MMMM YYYY")}
                                        </div>
                                        <button
                                            onClick={increaseMonth}
                                            disabled={nextMonthButtonDisabled}
                                            type="button"
                                            className="p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-colors"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                            />
                            <div className="today-clear-buttons">
                                <button
                                    type="button"
                                    onClick={selectToday}
                                    className="btn-today"
                                >
                                    Today
                                </button>
                                <button
                                    type="button"
                                    onClick={clearDate}
                                    className="btn-clear"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

                {error && (
                    <p className="mt-1 text-xs text-red-500">{error}</p>
                )}
            </div>
        </>
    );
};

export default DateTimeInputwithcal;